import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors'
import type { PromptsModerationAction } from './contentSafety'

export const getErrorCode = (data: any) => getErrorData(data)?.name

// Structural rather than `instanceof ORPCError`: the lockfile has multiple
// @orpc/client versions and ORPCError's hasInstance registry is per-version.
const isORPCErrorShape = (
  err: unknown,
): err is { code: string; status: number; message?: string } =>
  !!err &&
  typeof err === 'object' &&
  typeof (err as { code?: unknown }).code === 'string' &&
  typeof (err as { status?: unknown }).status === 'number'

export const getErrorData = (err: any) => {
  // v4: CombinedGraphQLErrors replaces ApolloError for GraphQL errors
  if (CombinedGraphQLErrors.is(err)) {
    return (err.errors?.[0]?.extensions as any)?.exception
  }
  if (isORPCErrorShape(err)) {
    return {
      name: err.code,
      message: typeof err.message === 'string' ? err.message : undefined,
    }
  }
  return err?.res?.data?.errors?.[0]?.extensions?.exception
}

/**
 * Classify an error into a descriptive telemetry code. Server-originated
 * codes are returned bare; codes derived on the client carry a
 * `CLIENT_DERIVED_` prefix so they are clearly distinguishable in analytics.
 *
 * Falls back through:
 *   1. GraphQL extensions.exception.name (server-thrown, no prefix)
 *   2. GRAPHQL_<code> from extensions.code (server-thrown, no prefix)
 *   3. HTTP_<status> for Apollo ServerError (server-thrown, no prefix)
 *   4. RESPONSE_PARSE_ERROR_<status> for ServerParseError (server-thrown)
 *   5. CLIENT_DERIVED_ABORTED / CLIENT_DERIVED_TIMEOUT for DOM aborts
 *   6. CLIENT_DERIVED_NETWORK_ERROR for fetch / network failures
 *   7. CLIENT_DERIVED_<ErrorName> for custom error classes
 *   8. CLIENT_DERIVED_UNKNOWN as a last resort (caller should capture to Sentry)
 *
 * Used to replace the bare `getErrorCode` for analytics so that previously-
 * "(none)" buckets become actionable categories.
 */
export const classifyErrorForTelemetry = (err: any): string => {
  if (err == null) return 'CLIENT_DERIVED_UNKNOWN'

  const data = getErrorData(err)
  if (data?.name && typeof data.name === 'string') return data.name

  // GraphQL error with no `.name` on extensions.exception — fall back to code
  if (CombinedGraphQLErrors.is(err)) {
    const code = (err.errors?.[0]?.extensions as any)?.code
    if (typeof code === 'string') return `GRAPHQL_${code}`
    return 'GRAPHQL_UNKNOWN'
  }
  const restCode = err?.res?.data?.errors?.[0]?.extensions?.code
  if (typeof restCode === 'string') return `GRAPHQL_${restCode}`

  if (ServerError.is(err)) return `HTTP_${err.statusCode}`
  if (ServerParseError.is(err)) return `RESPONSE_PARSE_ERROR_${err.statusCode}`

  // DOM AbortController, fetch timeout, etc.
  if (err?.name === 'AbortError') return 'CLIENT_DERIVED_ABORTED'
  if (err?.name === 'TimeoutError') return 'CLIENT_DERIVED_TIMEOUT'

  // `TypeError: Failed to fetch` (offline / DNS / CORS / cancelled)
  if (
    err instanceof TypeError &&
    typeof err.message === 'string' &&
    /fetch|network/i.test(err.message)
  ) {
    return 'CLIENT_DERIVED_NETWORK_ERROR'
  }

  // Drop generic Error/TypeError names which would just give us back "Error"
  if (
    typeof err?.name === 'string' &&
    err.name !== 'Error' &&
    err.name !== 'TypeError'
  ) {
    return `CLIENT_DERIVED_${err.name}`
  }

  return 'CLIENT_DERIVED_UNKNOWN'
}

/**
 * Currently superseded by promptFilterAction
 * Blocked info won't be returned anymore
 */
export function extractBlockedInfoFromNsfwError(err: any) {
  const errCode = getErrorCode(err)
  if (errCode !== 'NSFW_PROMPTS_DETECTED') {
    return { blockedReason: undefined, blockedKeywords: undefined }
  }
  const { blockedReason, blockedKeywords } =
    err?.res?.data?.errors?.[0]?.extensions?.exception ?? {}
  return {
    blockedReason,
    blockedKeywords: Array.isArray(blockedKeywords)
      ? blockedKeywords.join(', ')
      : blockedKeywords,
  }
}

export function extractPromptsModerationActionFromNsfwError(err: any) {
  const errCode = getErrorCode(err)
  if (errCode !== 'NSFW_PROMPTS_DETECTED') {
    return undefined
  }
  const { promptsModerationAction } =
    err?.res?.data?.errors?.[0]?.extensions?.exception ?? {}
  return promptsModerationAction as PromptsModerationAction
}
